// src/components/profiles/filters/FilterManager.jsx
import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Collapse,
  Stack,
  Tooltip
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// IMPORT FIX: Change named imports to default imports
import SelfDetailsFilter from './SelfDetailsFilter';
import AdvancedFilters from './AdvancedFilters';
import ActiveFiltersDisplay from './ActiveFiltersDisplay';
import { applyFilters } from './FilterLogic';
import { extractFilterOptions } from './FilterUtils'; // Removed calculateAgeStats
import { DEFAULT_FILTERS } from './types';

const FilterManager = ({ profiles, onFiltersChange }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Extract unique options from profiles
  const filterOptions = useMemo(() => {
    return extractFilterOptions(profiles);
  }, [profiles]);

  // Apply filters and notify parent
  const applyAndNotify = useCallback((updatedFilters) => {
    const result = applyFilters(profiles, updatedFilters);
    if (onFiltersChange) {
      onFiltersChange({
        results: result,
        appliedFilters: updatedFilters
      });
    }
  }, [profiles, onFiltersChange]);

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    const updatedFilters = {
      ...filters,
      [filterType]: value
    };
    
    setFilters(updatedFilters);
    applyAndNotify(updatedFilters);
  };

  // Handle self details change
  const handleSelfDetailsChange = (selfDetails) => {
    const updatedFilters = {
      ...filters,
      selfDetails
    };
    
    setFilters(updatedFilters);
    applyAndNotify(updatedFilters);
  };

  // Handle advanced filters change
  const handleAdvancedChange = (advancedFilters) => {
    const updatedFilters = {
      ...filters,
      ...advancedFilters
    };
    
    setFilters(updatedFilters);
    applyAndNotify(updatedFilters);
  };

  // Reset all filters
  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    applyAndNotify(DEFAULT_FILTERS);
  };

  // Remove specific filter
  const handleRemoveFilter = (filterType) => {
    const updatedFilters = {
      ...filters,
      [filterType]: DEFAULT_FILTERS[filterType]
    };
    
    setFilters(updatedFilters);
    applyAndNotify(updatedFilters);
  };

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    
    // Basic filters
    if (filters.gender && filters.gender !== 'all') count++;
    if (filters.ageRange[0] !== 18 || filters.ageRange[1] !== 60) count++;
    if (filters.city) count++;
    if (filters.profession) count++;
    if (filters.religion) count++;
    if (filters.education) count++;
    if (filters.maritalStatus !== 'all') count++;
    if (filters.verified) count++;
    
    // Self details filters
    if (filters.selfDetails && filters.selfDetails.enabled) {
      if (filters.selfDetails.gender) count++;
      if (filters.selfDetails.ageRange[0] !== 18 || filters.selfDetails.ageRange[1] !== 60) count++;
    }
    
    return count;
  }, [filters]);

  return (
    <Box>
      {/* Filter Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip 
              label={`${activeFiltersCount} active`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Advanced Filters">
            <IconButton
              onClick={() => setShowAdvanced(!showAdvanced)}
              color={showAdvanced ? 'primary' : 'default'}
            >
              {showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              <TuneIcon sx={{ ml: 0.5 }} />
            </IconButton>
          </Tooltip>
          
          {activeFiltersCount > 0 && (
            <Button
              variant="text"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              size="small"
            >
              Clear All
            </Button>
          )}
        </Box>
      </Box>

      {/* Active Filters Display */}
      <ActiveFiltersDisplay
        filters={filters}
        onRemove={handleRemoveFilter}
      />

      {/* Main Filters */}
      <Grid container spacing={3}>
        {/* Self Details Filter */}
        <Grid item xs={12} md={4}>
          <SelfDetailsFilter
            selfDetails={filters.selfDetails}
            onChange={handleSelfDetailsChange}
            options={filterOptions}
          />
        </Grid>

        {/* Basic Filters */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Basic Filters
            </Typography>
            
            <Grid container spacing={2}>
              {/* Gender Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Gender
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {['all', 'male', 'female'].map((gender) => (
                      <Chip
                        key={gender}
                        label={gender === 'all' ? 'All' : gender === 'male' ? 'Male' : 'Female'}
                        onClick={() => handleFilterChange('gender', gender)}
                        color={filters.gender === gender ? 'primary' : 'default'}
                        variant={filters.gender === gender ? 'filled' : 'outlined'}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>

              {/* Age Range */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Age: {filters.ageRange[0]} - {filters.ageRange[1]}
                  </Typography>
                  <input
                    type="range"
                    min="18"
                    max="60"
                    value={filters.ageRange[0]}
                    onChange={(e) => handleFilterChange('ageRange', [parseInt(e.target.value), filters.ageRange[1]])}
                    style={{ width: '100%',
                      accentColor: "#4b0082", // brand color
                     }}
                  />
                  <input
                    type="range"
                    min="18"
                    max="60"
                    value={filters.ageRange[1]}
                    onChange={(e) => handleFilterChange('ageRange', [filters.ageRange[0], parseInt(e.target.value)])}
                    style={{ width: '100%', 
                      accentColor: "#4b0082", // brand color
                     }}
                  />
                </Box>
              </Grid>

              {/* Marital Status */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Marital Status
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                    {['all', 'Single', 'Married', 'Divorced', 'Widowed'].map((status) => (
                      <Chip
                        key={status}
                        label={status === 'all' ? 'All' : status}
                        onClick={() => handleFilterChange('maritalStatus', status === 'all' ? 'all' : status)}
                        color={filters.maritalStatus === status ? 'primary' : 'default'}
                        variant={filters.maritalStatus === status ? 'filled' : 'outlined'}
                        size="small"
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>

              {/* City Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    City
                  </Typography>
                  <select
                    value={filters.city || ''}
                    onChange={(e) => handleFilterChange('city', e.target.value || '')}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="">All Cities</option>
                    {filterOptions.cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </Box>
              </Grid>

              {/* Profession Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Profession
                  </Typography>
                  <select
                    value={filters.profession || ''}
                    onChange={(e) => handleFilterChange('profession', e.target.value || '')}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  >
                    <option value="">All Professions</option>
                    {filterOptions.professions.map((prof) => (
                      <option key={prof} value={prof}>{prof}</option>
                    ))}
                  </select>
                </Box>
              </Grid>

              {/* Verified Only */}
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Verification
                  </Typography>
                  <Chip
                    label="Verified Only"
                    onClick={() => handleFilterChange('verified', !filters.verified)}
                    color={filters.verified ? 'primary' : 'default'}
                    variant={filters.verified ? 'filled' : 'outlined'}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Advanced Filters */}
      <Collapse in={showAdvanced}>
        <Box sx={{ mt: 3 }}>
          <AdvancedFilters
            filters={filters}
            onChange={handleAdvancedChange}
            options={filterOptions}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default FilterManager;